/**
 * L'Atelier de Lulu — Theme JavaScript
 * Animations, interactions & Shopify cart
 */

'use strict';

// ─── Utils ────────────────────────────────────────────────────────────────────

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

// ─── Page Loader ──────────────────────────────────────────────────────────────

const PageLoader = {
  el: null,
  init() {
    this.el = $('#page-loader');
    if (!this.el) return;
    window.addEventListener('load', () => this.hide(), { once: true });
    // Fallback timeout
    setTimeout(() => this.hide(), 3000);
  },
  hide() {
    if (!this.el) return;
    document.documentElement.classList.remove('no-js');
    this.el.classList.add('loaded');
    document.body.classList.add('page-loaded');
  }
};

// ─── Header Scroll ────────────────────────────────────────────────────────────

const Header = {
  el: null,
  init() {
    this.el = $('.site-header');
    if (!this.el) return;
    this.onScroll();
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
  },
  onScroll() {
    this.el.classList.toggle('scrolled', window.scrollY > 20);
  }
};

// ─── Mobile Menu ──────────────────────────────────────────────────────────────

const MobileMenu = {
  toggle: null,
  menu: null,
  overlay: null,
  closeBtn: null,

  init() {
    this.toggle  = $('#menu-toggle');
    this.menu    = $('#mobile-nav');
    this.overlay = $('#mobile-nav-overlay');
    this.closeBtn = $('#mobile-nav-close');
    if (!this.toggle || !this.menu) return;

    this.toggle.addEventListener('click', () => {
      this.isOpen ? this.close() : this.openMenu();
    });

    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    this.menu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        setTimeout(() => this.close(), 150);
      });
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  },

  get isOpen() { return this.menu.classList.contains('open'); },

  openMenu() {
    this.menu.classList.add('open');
    this.menu.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('open');
    this.toggle.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.toggle.setAttribute('aria-expanded', 'true');
    this.toggle.setAttribute('aria-label', 'Fermer le menu');
  },

  close() {
    this.menu.classList.remove('open');
    this.menu.setAttribute('aria-hidden', 'true');
    this.overlay?.classList.remove('open');
    this.toggle.classList.remove('open');
    document.body.style.overflow = '';
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-label', 'Ouvrir le menu');
  }
};

// ─── Cart Drawer ──────────────────────────────────────────────────────────────

const CartDrawer = {
  drawer: null,
  overlay: null,
  countEls: [],

  init() {
    this.drawer  = $('#cart-drawer');
    this.overlay = $('#cart-overlay');
    this.countEls = $$('.cart-count');
    if (!this.drawer) return;

    // Ouvrir panier
    $$('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', e => { e.preventDefault(); this.open(); });
    });

    // Fermer
    $$('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Boutons quantité dans le panier
    this.drawer.addEventListener('click', e => {
      const btn = e.target.closest('[data-cart-qty]');
      if (!btn) return;
      const action = btn.dataset.cartQty;
      const lineKey = btn.closest('[data-line-key]')?.dataset.lineKey;
      const valEl   = btn.closest('.cart-item__qty')?.querySelector('.cart-item__qty-val');
      if (!lineKey || !valEl) return;

      let qty = parseInt(valEl.textContent, 10);
      qty = action === '+' ? qty + 1 : Math.max(0, qty - 1);
      this.updateQuantity(lineKey, qty);
    });

    // Supprimer article
    this.drawer.addEventListener('click', e => {
      const btn = e.target.closest('[data-cart-remove]');
      if (!btn) return;
      const lineKey = btn.dataset.cartRemove;
      this.updateQuantity(lineKey, 0);
    });

    // Charger le panier initial
    this.fetchCart();
  },

  get isOpen() { return this.drawer?.classList.contains('open'); },

  open() {
    this.drawer?.classList.add('open');
    this.overlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.drawer?.classList.remove('open');
    this.overlay?.classList.remove('open');
    document.body.style.overflow = '';
  },

  async fetchCart() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
      this.updateCount(cart.item_count);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  },

  async addToCart(variantId, qty = 1, properties = {}) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty, properties })
      });
      const item = await res.json();
      if (item.status) throw new Error(item.description);

      await this.fetchCart();
      CartNotification.show(item);
      this.bumpCount();
    } catch (err) {
      console.error('Add to cart error:', err);
      alert(err.message || 'Erreur lors de l\'ajout au panier.');
    }
  },

  async updateQuantity(lineKey, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });
      await this.fetchCart();
    } catch (err) {
      console.error('Cart update error:', err);
    }
  },

  updateCount(count) {
    this.countEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  },

  bumpCount() {
    this.countEls.forEach(el => {
      el.classList.remove('bump');
      void el.offsetWidth;
      el.classList.add('bump');
      setTimeout(() => el.classList.remove('bump'), 300);
    });
  },

  render(cart) {
    const body = this.drawer?.querySelector('.cart-drawer__body');
    const footer = this.drawer?.querySelector('.cart-drawer__footer');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 01-8 0"/>
          </svg>
          <p>Votre panier est vide</p>
          <a href="/collections/all" class="btn btn--primary btn--sm" onclick="CartDrawer.close()">
            Découvrir la boutique
          </a>
        </div>`;
      if (footer) footer.style.display = 'none';
      return;
    }

    if (footer) footer.style.display = 'block';

    body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-line-key="${item.key}">
        <img
          class="cart-item__img"
          src="${item.image ? item.image.replace('_grande', '_compact') : ''}"
          alt="${item.product_title}"
          loading="lazy"
        >
        <div class="cart-item__info">
          <div class="cart-item__title">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-item__variant">${item.variant_title}</div>`
            : ''}
          ${item.properties && Object.keys(item.properties).length > 0
            ? Object.entries(item.properties).map(([k, v]) =>
                `<div class="cart-item__variant" style="font-style:italic;margin-top:2px;"><strong>${k}:</strong> ${v}</div>`
              ).join('')
            : ''}
          <div class="cart-item__qty">
            <button class="cart-item__qty-btn" data-cart-qty="-">−</button>
            <span class="cart-item__qty-val">${item.quantity}</span>
            <button class="cart-item__qty-btn" data-cart-qty="+">+</button>
          </div>
          <button class="cart-item__remove" data-cart-remove="${item.key}">Supprimer</button>
        </div>
        <div class="cart-item__price">${this.formatMoney(item.line_price)}</div>
      </div>
    `).join('');

    // Mettre à jour le sous-total
    const subtotalEl = this.drawer?.querySelector('.cart-subtotal-price');
    if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);
  },

  formatMoney(cents) {
    return (cents / 100).toLocaleString('fr-FR', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    });
  }
};

// Exposer globalement pour les liens onclick
window.CartDrawer = CartDrawer;

// ─── Cart Notification ────────────────────────────────────────────────────────

const CartNotification = {
  el: null,
  timer: null,

  init() {
    this.el = $('#cart-notification');
  },

  show(item) {
    if (!this.el) return;
    const img   = this.el.querySelector('.cart-notification__img');
    const title = this.el.querySelector('.cart-notification__title');
    if (img)   img.src = item.image ? item.image.replace('_grande', '_compact') : '';
    if (img)   img.alt = item.product_title;
    if (title) title.textContent = item.product_title;

    this.el.classList.add('show');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.hide(), 4000);
  },

  hide() {
    this.el?.classList.remove('show');
  }
};

// ─── Scroll Reveal ────────────────────────────────────────────────────────────

const ScrollReveal = {
  observer: null,

  init() {
    if (!('IntersectionObserver' in window)) {
      $$('.reveal').forEach(el => el.classList.add('revealed'));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    $$('.reveal').forEach(el => this.observer.observe(el));
  }
};

// ─── Back to Top ──────────────────────────────────────────────────────────────

const BackToTop = {
  btn: null,

  init() {
    this.btn = $('#back-to-top');
    if (!this.btn) return;

    window.addEventListener('scroll', () => {
      this.btn.classList.toggle('visible', window.scrollY > 400);
    }, { passive: true });

    this.btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
};

// ─── Cookie Banner ────────────────────────────────────────────────────────────

const CookieBanner = {
  el: null,
  STORAGE_KEY: 'lulu_cookies_accepted',

  init() {
    this.el = $('#cookie-banner');
    if (!this.el) return;

    if (localStorage.getItem(this.STORAGE_KEY)) return;

    setTimeout(() => this.el.classList.add('show'), 1200);

    $('#cookie-accept')?.addEventListener('click', () => {
      localStorage.setItem(this.STORAGE_KEY, 'all');
      this.hide();
    });

    $('#cookie-decline')?.addEventListener('click', () => {
      localStorage.setItem(this.STORAGE_KEY, 'essential');
      this.hide();
    });

    $('#cookie-settings')?.addEventListener('click', () => {
      window.location.href = '/pages/cookies';
    });
  },

  hide() {
    this.el.classList.remove('show');
    setTimeout(() => this.el.remove(), 500);
  }
};

// ─── Product Gallery ──────────────────────────────────────────────────────────

const ProductGallery = {
  init() {
    const thumbs  = $$('.product-gallery__thumb');
    const mainImg = $('.product-gallery__main-img');
    if (!thumbs.length || !mainImg) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');

        mainImg.style.opacity = '0';
        mainImg.style.transform = 'scale(0.98)';
        setTimeout(() => {
          mainImg.src = thumb.dataset.fullSrc || thumb.src;
          mainImg.style.opacity = '1';
          mainImg.style.transform = 'scale(1)';
        }, 200);
      });
    });

    mainImg.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
  }
};

// ─── Accordéons ───────────────────────────────────────────────────────────────

const Accordion = {
  init() {
    $$('.accordion-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const item    = btn.closest('.accordion-item');
        const isOpen  = item.classList.contains('open');

        // Fermer tous
        $$('.accordion-item.open').forEach(i => i.classList.remove('open'));

        // Ouvrir celui cliqué si était fermé
        if (!isOpen) item.classList.add('open');
      });
    });
  }
};

// ─── Hero Ken Burns ───────────────────────────────────────────────────────────

const Hero = {
  init() {
    const hero = $('.hero');
    if (!hero) return;

    // Déclencher l'animation Ken Burns
    setTimeout(() => hero.classList.add('loaded'), 100);
  }
};

// ─── Quantity Selector (page produit) ────────────────────────────────────────

const QuantitySelector = {
  init() {
    $$('[data-qty-decrease]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.product-qty')?.querySelector('.product-qty__val');
        if (!input) return;
        const val = parseInt(input.value || input.textContent, 10);
        if (val > 1) {
          input.tagName === 'INPUT' ? (input.value = val - 1) : (input.textContent = val - 1);
        }
      });
    });

    $$('[data-qty-increase]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.product-qty')?.querySelector('.product-qty__val');
        if (!input) return;
        const val = parseInt(input.value || input.textContent, 10);
        input.tagName === 'INPUT' ? (input.value = val + 1) : (input.textContent = val + 1);
      });
    });
  }
};

// ─── Formulaire de contact ────────────────────────────────────────────────────

const ContactForm = {
  init() {
    const form = $('#contact-form');
    if (!form) return;

    // Floating labels
    form.querySelectorAll('.form-input, .form-textarea, .form-select').forEach(input => {
      input.addEventListener('focus', () => input.parentElement.classList.add('focused'));
      input.addEventListener('blur', () => {
        if (!input.value) input.parentElement.classList.remove('focused');
      });
      if (input.value) input.parentElement.classList.add('focused');
    });
  }
};

// ─── Marquee pause on hover ───────────────────────────────────────────────────

const Marquee = {
  init() {
    $$('.marquee-track').forEach(track => {
      // Dupliquer le contenu pour l'animation infinie
      track.innerHTML += track.innerHTML;

      const section = track.closest('.marquee-section');
      section?.addEventListener('mouseenter', () => {
        track.style.animationPlayState = 'paused';
      });
      section?.addEventListener('mouseleave', () => {
        track.style.animationPlayState = 'running';
      });
    });
  }
};

// ─── Wishlist (localStorage) ──────────────────────────────────────────────────

const Wishlist = {
  STORAGE_KEY: 'lulu_wishlist',

  get items() {
    return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
  },

  toggle(productId) {
    const items = this.items;
    const idx   = items.indexOf(productId);
    if (idx > -1) {
      items.splice(idx, 1);
    } else {
      items.push(productId);
    }
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    return idx === -1; // true si ajouté
  },

  isInList(productId) {
    return this.items.includes(productId);
  },

  init() {
    $$('[data-wishlist-btn]').forEach(btn => {
      const id = btn.dataset.wishlistBtn;
      if (this.isInList(id)) btn.classList.add('active');

      btn.addEventListener('click', () => {
        const added = this.toggle(id);
        btn.classList.toggle('active', added);

        // Animation coeur
        btn.style.transform = 'scale(1.3)';
        setTimeout(() => btn.style.transform = '', 200);
      });
    });
  }
};

// ─── Add to cart (page produit) ───────────────────────────────────────────────

const ProductPage = {
  init() {
    const form = $('#product-form');
    if (!form) return;

    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn       = form.querySelector('[type="submit"]');
      const variantId = form.querySelector('[name="id"]')?.value;
      const qty       = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);

      if (!variantId) return;

      const properties = {};
      form.querySelectorAll('[name^="properties"]').forEach(field => {
        const val = field.value.trim();
        if (val) {
          const key = field.name.replace('properties[', '').replace(']', '');
          properties[key] = val;
        }
      });

      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Ajout…';

      await CartDrawer.addToCart(variantId, qty, properties);
      CartDrawer.open();

      btn.disabled = false;
      btn.textContent = originalText;
    });

    // Quick Add (grille produit)
    document.addEventListener('click', async e => {
      const quickBtn = e.target.closest('[data-quick-add]');
      if (!quickBtn) return;
      e.preventDefault();

      const variantId = quickBtn.dataset.quickAdd;
      quickBtn.disabled = true;
      await CartDrawer.addToCart(variantId, 1);
      quickBtn.disabled = false;
    });
  }
};

// ─── Smooth page transitions ──────────────────────────────────────────────────

const PageTransitions = {
  init() {
    document.addEventListener('click', e => {
      const link = e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');

      // Ignorer liens externes, ancres, etc.
      if (!href || href.startsWith('#') || href.startsWith('http') ||
          href.startsWith('mailto') || href.startsWith('tel') ||
          link.target === '_blank' || e.ctrlKey || e.metaKey || e.shiftKey) return;

      // Ignorer si data-no-transition
      if (link.dataset.noTransition !== undefined) return;

      e.preventDefault();

      const loader = $('#page-loader');
      if (loader) {
        loader.classList.remove('loaded');
        loader.style.opacity = '1';
        loader.style.visibility = 'visible';
      }

      setTimeout(() => {
        window.location.href = href;
      }, 300);
    });
  }
};

// ─── Newsletter popup ─────────────────────────────────────────────────────────

const NewsletterPopup = {
  STORAGE_KEY: 'lulu_newsletter_seen',
  el: null,

  init() {
    this.el = $('#newsletter-popup');
    if (!this.el) return;
    if (localStorage.getItem(this.STORAGE_KEY)) return;

    // Afficher après 8 secondes
    setTimeout(() => this.show(), 8000);

    this.el.querySelector('[data-popup-close]')?.addEventListener('click', () => this.close());
    this.el.querySelector('[data-popup-overlay]')?.addEventListener('click', () => this.close());
  },

  show() {
    this.el.classList.add('show');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.el.classList.remove('show');
    document.body.style.overflow = '';
    localStorage.setItem(this.STORAGE_KEY, '1');
  }
};

// ─── Image lazy loading amélioré ──────────────────────────────────────────────

const LazyImages = {
  init() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          observer.unobserve(img);
        });
      },
      { rootMargin: '200px' }
    );

    $$('img[data-src]').forEach(img => observer.observe(img));
  }
};

// ─── Search Overlay ───────────────────────────────────────────────────────────

const SearchOverlay = {
  overlay: null,
  input: null,
  grid: null,
  emptyState: null,
  noResults: null,
  debounceTimer: null,

  init() {
    this.overlay    = $('#search-overlay');
    this.input      = $('#search-input');
    this.grid       = $('#search-grid');
    this.emptyState = $('#search-empty');
    this.noResults  = $('#search-no-results');
    if (!this.overlay) return;

    $$('[data-search-toggle]').forEach(btn => {
      btn.addEventListener('click', e => { e.preventDefault(); this.open(); });
    });

    $$('[data-search-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) this.close();
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        this.isOpen ? this.close() : this.open();
      }
    });

    this.input?.addEventListener('input', () => this.onInput());

    $('#search-form')?.addEventListener('submit', e => {
      if (!this.input?.value.trim()) e.preventDefault();
    });
  },

  get isOpen() { return this.overlay?.classList.contains('open'); },

  open() {
    this.overlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.input?.focus(), 100);
  },

  close() {
    this.overlay?.classList.remove('open');
    document.body.style.overflow = '';
    if (this.input) this.input.value = '';
    this.resetResults();
  },

  resetResults() {
    if (this.grid) this.grid.innerHTML = '';
    if (this.emptyState) this.emptyState.style.display = '';
    if (this.noResults) this.noResults.style.display = 'none';
  },

  onInput() {
    clearTimeout(this.debounceTimer);
    const query = this.input?.value.trim();
    if (!query || query.length < 2) {
      this.resetResults();
      return;
    }
    this.debounceTimer = setTimeout(() => this.search(query), 300);
  },

  async search(query) {
    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=12`
      );
      const data = await res.json();
      const products = data?.resources?.results?.products || [];
      this.renderResults(products);
    } catch (err) {
      try {
        const res = await fetch(
          `/search?q=${encodeURIComponent(query)}&type=product&view=json`
        );
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const items = doc.querySelectorAll('.product-card, [data-product-card]');
        if (items.length === 0) {
          this.renderResults([]);
        }
      } catch {
        console.error('Search error:', err);
      }
    }
  },

  renderResults(products) {
    if (!this.grid) return;

    if (products.length === 0) {
      this.grid.innerHTML = '';
      if (this.emptyState) this.emptyState.style.display = 'none';
      if (this.noResults) this.noResults.style.display = '';
      return;
    }

    if (this.emptyState) this.emptyState.style.display = 'none';
    if (this.noResults) this.noResults.style.display = 'none';

    this.grid.innerHTML = products.map((p, i) => `
      <a href="${p.url}" class="search-result-card" style="animation: fadeUp 0.4s var(--ease-out) ${i * 0.05}s both;">
        <img
          class="search-result-card__img"
          src="${p.image || p.featured_image?.url || ''}"
          alt="${p.title}"
          loading="lazy"
        >
        <div class="search-result-card__info">
          <div class="search-result-card__title">${p.title}</div>
          <div class="search-result-card__price">${this.formatPrice(p.price)}</div>
        </div>
      </a>
    `).join('');
  },

  formatPrice(price) {
    if (!price) return '';
    const amount = typeof price === 'string' ? parseFloat(price) : price;
    const cents = amount > 1000 ? amount / 100 : amount;
    return cents.toLocaleString('fr-FR', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    });
  }
};

// ─── Custom Color Counter ─────────────────────────────────────────────────────

const CustomColorCounter = {
  init() {
    const textarea = $('#custom-message');
    const counter  = $('#custom-color-count');
    if (!textarea || !counter) return;

    textarea.addEventListener('input', () => {
      counter.textContent = textarea.value.length;
    });
  }
};

// ─── Stagger Grid Reveal ──────────────────────────────────────────────────────

const StaggerReveal = {
  observer: null,

  init() {
    if (!('IntersectionObserver' in window)) {
      $$('.products-grid .product-card, .collections-grid .collection-card, .testimonials-grid .testimonial-card, .footer-col').forEach(el => el.classList.add('revealed'));
      return;
    }

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const parent = entry.target.closest('.products-grid, .collections-grid, .testimonials-grid, .footer-top');
            if (parent) {
              const items = parent.querySelectorAll('.product-card, .collection-card, .testimonial-card, .footer-col');
              items.forEach((item, i) => {
                setTimeout(() => item.classList.add('revealed'), i * 80);
              });
              items.forEach(item => this.observer.unobserve(item));
            } else {
              entry.target.classList.add('revealed');
              this.observer.unobserve(entry.target);
            }
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );

    $$('.products-grid .product-card, .collections-grid .collection-card, .testimonials-grid .testimonial-card, .footer-col').forEach(el => this.observer.observe(el));
  }
};

// ─── Smooth Section Header Reveal ─────────────────────────────────────────────

const SectionHeaderReveal = {
  init() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    $$('.section-header').forEach(el => observer.observe(el));
  }
};

// ─── Initialisation ───────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  PageLoader.init();
  Header.init();
  MobileMenu.init();
  CartDrawer.init();
  CartNotification.init();
  ScrollReveal.init();
  BackToTop.init();
  CookieBanner.init();
  ProductGallery.init();
  Accordion.init();
  Hero.init();
  QuantitySelector.init();
  ContactForm.init();
  Marquee.init();
  Wishlist.init();
  ProductPage.init();
  NewsletterPopup.init();
  LazyImages.init();
  SearchOverlay.init();
  CustomColorCounter.init();
  StaggerReveal.init();
  SectionHeaderReveal.init();

  document.documentElement.classList.remove('no-js');
});
